import { useState } from "react";
import { motion } from "framer-motion";
import { X, Phone, Loader2 } from "lucide-react";
import { CRM } from "../../config/content";
import { logCall } from "../../lib/api";
import { toast } from "sonner";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-[#17231B] last:border-0">
      <span className="text-xs uppercase tracking-wider text-[#6B7A70] shrink-0">{label}</span>
      <span className="text-sm text-white text-right">{value || "—"}</span>
    </div>
  );
}

export default function LeadDrawer({ lead, onClose, onUpdated }) {
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submitCall = async () => {
    if (!outcome) return;
    setSaving(true);
    try {
      const updated = await logCall(lead.id, outcome, note.trim());
      onUpdated(updated);
      setOutcome("");
      setNote("");
      toast.success("Call logged");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const timeline = [...(lead.timeline || [])].reverse();

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <motion.aside
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[440px] bg-[#0B110D] border-l border-[#22C55E]/[0.18] overflow-y-auto"
        data-testid="lead-drawer"
        data-lenis-prevent
      >
        <div className="sticky top-0 z-10 bg-[#0B110D]/95 backdrop-blur border-b border-[#17231B] px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-heading uppercase text-2xl text-white tracking-wide" data-testid="drawer-name">{lead.name || "(no name)"}</h2>
            <p className="text-xs text-[#6B7A70]">{lead.phone ? `+91 ${lead.phone}` : "No phone"} · {lead.city || "—"}</p>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="drawer-close" className="w-9 h-9 rounded-full border border-[#22C55E]/25 flex items-center justify-center text-[#A7B3AA] hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-5">
          <div className="flex flex-wrap gap-2 mb-5">
            <span className={`rounded-full text-xs font-bold px-3 py-1 border ${lead.status === "hot" ? "text-[#EF4444] border-[#EF4444]/40 bg-[#EF4444]/10" : lead.status === "cold" ? "text-slate-400 border-slate-500/40 bg-slate-500/10" : "text-[#FACC15] border-[#FACC15]/40 bg-[#FACC15]/10"}`} data-testid="drawer-status">
              {lead.status?.toUpperCase()}
            </span>
            <span className="rounded-full text-xs font-bold px-3 py-1 border text-[#22C55E] border-[#22C55E]/40 bg-[#22C55E]/10" data-testid="drawer-stage">{lead.stage}</span>
            {lead.form_state === "partial" && <span className="rounded-full text-xs font-bold px-3 py-1 border text-[#A7B3AA] border-white/15 bg-white/5">Partial form</span>}
            {lead.test_data && <span className="rounded-full text-xs font-bold px-3 py-1 border text-[#FACC15] border-[#FACC15]/40 bg-[#FACC15]/10">TEST DATA</span>}
          </div>

          <h3 className="font-heading uppercase text-white text-lg tracking-wide mb-2">Form Answers</h3>
          <div className="rounded-xl bg-[#0F1712] border border-[#22C55E]/[0.15] px-4 py-2" data-testid="drawer-answers">
            <Row label="Email" value={lead.email} />
            <Row label="Experience" value={lead.experience} />
            <Row label="Problems" value={(lead.problems || []).join("; ")} />
            <Row label="Markets" value={(lead.markets || []).join(", ")} />
            <Row label="Mode" value={lead.mode} />
            <Row label="Timing" value={lead.timing} />
            <Row label="Capital" value={lead.capital} />
            <Row label="Source" value={lead.utm_source ? `${lead.utm_source}${lead.utm_campaign ? ` / ${lead.utm_campaign}` : ""}` : "Direct"} />
            <Row label="Consent" value={lead.consent ? `Yes${lead.consent_time ? ` · ${fmt(lead.consent_time)}` : ""}` : "No"} />
            <Row label="Notes" value={lead.notes} />
            <Row label="Next follow-up" value={lead.next_follow_up} />
            <Row label="Last call" value={lead.last_call_outcome} />
            <Row label="Created" value={fmt(lead.created_at)} />
          </div>

          <h3 className="font-heading uppercase text-white text-lg tracking-wide mt-6 mb-2">Log a Call</h3>
          <div className="rounded-xl bg-[#0F1712] border border-[#22C55E]/[0.15] p-4 space-y-3" data-testid="drawer-log-call">
            <div className="grid grid-cols-2 gap-2">
              {CRM.callOutcomes.map((o) => (
                <button
                  key={o}
                  onClick={() => setOutcome(o)}
                  data-testid={`call-outcome-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`rounded-xl border px-3 py-2.5 text-xs font-bold transition-colors ${
                    outcome === o ? "bg-[#22C55E]/15 border-[#22C55E] text-white" : "bg-[#070A08] border-[#22C55E]/[0.18] text-[#A7B3AA] hover:border-[#22C55E]/50"
                  }`}
                >
                  {o}
                </button>
              ))}
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Short note about the call (optional)"
              rows={2}
              data-testid="call-note-input"
              className="w-full rounded-xl bg-[#070A08] border border-[#22C55E]/[0.18] focus:border-[#22C55E]/60 outline-none px-3 py-2.5 text-sm text-white placeholder:text-[#6B7A70] transition-colors resize-none"
            />
            <button
              onClick={submitCall}
              disabled={!outcome || saving}
              data-testid="log-call-button"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#22C55E] hover:bg-[#16A34A] disabled:opacity-40 text-[#04100A] font-extrabold text-sm px-5 py-3 transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
              Log call
            </button>
          </div>

          <h3 className="font-heading uppercase text-white text-lg tracking-wide mt-6 mb-2">Activity Timeline</h3>
          <div className="space-y-0" data-testid="drawer-timeline">
            {timeline.length === 0 && <p className="text-sm text-[#6B7A70]">No activity yet.</p>}
            {timeline.map((ev, i) => (
              <div key={i} className="flex gap-3 pb-4 relative" data-testid={`timeline-event-${i}`}>
                {i < timeline.length - 1 && <span className="absolute left-[5px] top-4 bottom-0 w-px bg-[#17231B]" aria-hidden="true" />}
                <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${ev.type === "call" ? "bg-[#22C55E]" : ev.type === "stage" ? "bg-[#FACC15]" : "bg-[#6B7A70]"}`} aria-hidden="true" />
                <div>
                  <p className="text-sm text-white leading-snug">{ev.text}</p>
                  <p className="text-xs text-[#6B7A70] mt-0.5">{fmt(ev.at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.aside>
    </>
  );
}
